import { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Field, Input, Select, Text, makeStyles, tokens, useId } from '@fluentui/react-components';
import { readTpmFailoverThreshold, stageTpmFailoverThreshold } from './routingModelConfiguration';

const useStyles = makeStyles({
  section: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    padding: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalS,
    minWidth: 0,
    flexShrink: 0,
    overflowWrap: 'anywhere',
  },
  header: {
    display: 'flex', alignItems: 'center', flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS, marginBottom: tokens.spacingVerticalXS,
    '& h3': { margin: 0 },
  },
  controls: {
    display: 'flex', alignItems: 'end', flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM, marginTop: tokens.spacingVerticalS,
  },
  field: { minWidth: 0, maxWidth: '100%' },
  percentage: { width: '112px', maxWidth: '100%' },
  helper: { color: tokens.colorNeutralForeground2 },
  notice: { marginTop: tokens.spacingVerticalXS },
  details: {
    marginTop: tokens.spacingVerticalS, fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    '& summary': { cursor: 'pointer' },
  },
});

export interface RoutingTpmThresholdProps {
  environment: string | null;
  baseSavedJson: string;
  baseDraftJson: string;
  savedJson: string;
  draftJson: string;
  editable: boolean;
  editing: boolean;
  busy: boolean;
  onChange: (json: string) => void;
  onValidationChange: (error: string | null) => void;
}

interface Props extends RoutingTpmThresholdProps {
  runtimePercent?: number;
  runtimeExpected?: boolean;
  runtimeChecking?: boolean;
}

export function RoutingTpmThresholdControl({
  environment, baseSavedJson, baseDraftJson, savedJson, draftJson, editable, editing, busy,
  onChange, onValidationChange, runtimePercent, runtimeExpected = false, runtimeChecking = false,
}: Props) {
  const styles = useStyles();
  const headingId = useId('tpm-failover');
  const helpId = useId('tpm-failover-help');
  const draft = useMemo(() => readTpmFailoverThreshold(
    environment === null ? draftJson : baseDraftJson, environment === null ? '{}' : draftJson,
  ), [environment, baseDraftJson, draftJson]);
  const saved = useMemo(() => readTpmFailoverThreshold(
    environment === null ? savedJson : baseSavedJson, environment === null ? '{}' : savedJson,
  ), [environment, baseSavedJson, savedJson]);
  const [text, setText] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  useEffect(() => {
    setText(draft.percent === null ? '' : String(draft.percent));
    setInputError(null);
    onValidationChange(null);
  }, [draft, editing, onValidationChange]);
  useEffect(() => () => onValidationChange(null), [onValidationChange]);

  const disabled = busy || !editable || !editing || !!draft.error;
  const hasOwnValue = draft.source === (environment === null ? 'base' : 'environment');
  const changed = !draft.error && !saved.error
    && (draft.percent !== saved.percent || draft.source !== saved.source);
  const validRuntime = typeof runtimePercent === 'number'
    && Number.isInteger(runtimePercent) && runtimePercent >= 1 && runtimePercent <= 100;
  const pending = validRuntime && !runtimeChecking && saved.percent !== null && runtimePercent !== saved.percent;
  const source = (from: typeof saved.source) => from === 'default' ? 'default'
    : from === 'base' ? environment === null ? 'Base' : 'inherited from Base'
    : environment ?? 'Base';
  const stage = (percent: number | null) => {
    if (disabled) return;
    try {
      const next = stageTpmFailoverThreshold(draftJson, percent);
      setInputError(null);
      onValidationChange(null);
      setText(percent === null ? '' : String(percent));
      onChange(next);
    } catch {
      const message = 'Could not update the threshold. Correct the JSON first; your draft was preserved.';
      setInputError(message);
      onValidationChange(message);
    }
  };

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <div className={styles.header}>
        <Text as="h3" id={headingId} size={300} weight="semibold">TPM failover</Text>
        <Badge appearance="outline">Saved: {saved.percent === null ? 'Invalid' : `${saved.percent}%`}</Badge>
        {!saved.error && <Text size={200} className={styles.helper}>{source(saved.source)}</Text>}
        {runtimeExpected && (
          <Badge appearance="outline" color={pending ? 'warning' : 'informative'}>
            Runtime: {runtimeChecking ? 'Checking…' : validRuntime ? `${runtimePercent}%` : 'Unavailable'}
          </Badge>
        )}
        {changed && <Badge appearance="tint" color="brand">Unsaved: {draft.percent}% · {source(draft.source)}</Badge>}
      </div>
      <Text id={helpId} size={200} block className={styles.helper}>
        Try another endpoint when projected TPM exceeds this share of its quota. Applies to all configured endpoints; Azure quotas stay unchanged.
      </Text>
      <div className={styles.controls}>
        <Field label="Failover preset" size="small" className={styles.field}>
          <Select
            size="small" disabled={disabled}
            value={draft.percent !== null && [80, 90, 100].includes(draft.percent) ? String(draft.percent) : 'custom'}
            aria-describedby={helpId}
            onChange={(_, data) => { if (data.value !== 'custom') stage(Number(data.value)); }}
          >
            <option value="80">80% — more headroom</option>
            <option value="90">90% — earlier failover</option>
            <option value="100">100% — default</option>
            <option value="custom" disabled>Custom percentage</option>
          </Select>
        </Field>
        <Field
          label="Threshold (1–100)" size="small" className={styles.field}
          validationState={inputError ? 'error' : 'none'} validationMessage={inputError ?? undefined}
        >
          <Input
            className={styles.percentage} size="small" type="number" min={1} max={100} step={1}
            value={text} contentAfter="%" disabled={disabled} aria-describedby={helpId}
            onChange={(_, data) => {
              setText(data.value);
              const percent = Number(data.value);
              if (!/^\d+$/.test(data.value) || !Number.isInteger(percent) || percent < 1 || percent > 100) {
                const message = 'Enter a whole percentage from 1 to 100.';
                setInputError(message);
                onValidationChange(message);
                return;
              }
              stage(percent);
            }}
          />
        </Field>
        <Button size="small" appearance="subtle" disabled={disabled || !hasOwnValue} onClick={() => stage(null)}>
          {environment === null ? 'Use default' : 'Use Base / default'}
        </Button>
      </div>
      <div aria-live="polite">
        {draft.error ? <Text size={200} block className={styles.notice}>{draft.error}</Text> : (
          <Text size={200} block className={styles.notice}>
            {!editable ? 'This environment is read-only.'
              : busy ? 'Wait for loading, saving, or model fetch to finish.'
              : !editing ? 'Turn on Edit mode below to change this setting.'
              : 'Changes are staged in this layer’s JSON. Use Save below to review and apply; no model fetch is needed.'}
          </Text>
        )}
        {pending && <Text size={200} block className={styles.notice}>Runtime has not confirmed the saved value. Use Check seed status to refresh; no model fetch is needed.</Text>}
        {environment !== null && baseDraftJson !== baseSavedJson && (
          <Text size={200} block className={styles.notice}>The preview includes unsaved Base edits. Saving this environment does not save Base.</Text>
        )}
      </div>
      <details className={styles.details}>
        <summary>How the threshold works</summary>
        <Text size={200} block>
          Best-effort check of tracked tokens plus the next request’s token estimate, not a live Azure usage measurement.
          Equality is allowed. Zero-quota endpoints or unavailable Redis tracking skip this gate;
          last-resort panic routing can bypass it. This is not a hard quota guarantee.
        </Text>
      </details>
    </section>
  );
}
